import type { Complexity, DevelopmentCategory, DevelopmentStatus } from '@/shared/utils/format-status.js';

export type JiraEpicSyncStatus = 'NONE' | 'IDLE' | 'PENDING' | 'RETRYING' | 'LINKED' | 'FAILED';
export type JiraStoriesSyncStatus = 'NONE' | 'GENERATING' | 'CREATING' | 'WAITING_EPIC' | 'RETRYING' | 'DONE' | 'FAILED';
export type JiraItemSyncStatus = 'NONE' | 'PENDING' | 'RETRYING' | 'LINKED' | 'FAILED';

export type JiraEpicSync = {
    status: JiraEpicSyncStatus;
    nextAttemptAt: string | null;
};

export type JiraStoriesSync = {
    status: JiraStoriesSyncStatus;
    created: number;
    total: number;
    nextAttemptAt: string | null;
};

export type JiraItemSync = {
    status: JiraItemSyncStatus;
    nextAttemptAt: string | null;
};

export type DevelopmentSummaryResponse = {
    id: string;
    displayId: string;
    title: string;
    status: DevelopmentStatus;
    createdAt: string;
    updatedAt: string;
    businessProcessName: string | null;
    consultorId: string | null;
    consultorName: string | null;
    usuarioChaveId: string | null;
    usuarioChaveName: string | null;
    projectName: string;
    projectAbbreviation: string;
    clientName: string;
    clientAbbreviation: string;
    functionalComplexity: Complexity | null;
    techComplexity: Complexity | null;
    estimatedDeliveryDate: string | null;
    startDate: string | null;
    devEndDate: string | null;
    efValidatedAt: string | null;
    category: DevelopmentCategory | null;
    jiraEpicKey?: string | null;
    jiraEpicUrl?: string | null;
    jiraEpicSync?: JiraEpicSync;
};

export type DevelopmentSummaryProps = {
    id: string;
    displayId: string;
    title: string;
    status: DevelopmentStatus;
    createdAt: string;
    updatedAt: string;
    businessProcessName: string | null;
    consultorId: string | null;
    consultorName: string | null;
    keyUserId: string | null;
    keyUserName: string | null;
    projectName: string;
    projectAbbreviation: string;
    clientName: string;
    clientAbbreviation: string;
    functionalComplexity: Complexity | null;
    techComplexity: Complexity | null;
    estimatedDeliveryDate: string | null;
    startDate: string | null;
    devEndDate: string | null;
    efValidatedAt: string | null;
    category: DevelopmentCategory | null;
    jiraEpicKey?: string | null;
    jiraEpicUrl?: string | null;
    jiraEpicSync?: JiraEpicSync;
};

export class DevelopmentSummaryModel {
    private constructor(private readonly props: DevelopmentSummaryProps) {}

    public static with(props: DevelopmentSummaryProps): DevelopmentSummaryModel {
        return new DevelopmentSummaryModel(props);
    }

    public static withFromResponse(response: DevelopmentSummaryResponse): DevelopmentSummaryModel {
        return DevelopmentSummaryModel.with({
            id: response.id,
            displayId: response.displayId,
            title: response.title,
            status: response.status,
            createdAt: response.createdAt,
            updatedAt: response.updatedAt,
            businessProcessName: response.businessProcessName,
            consultorId: response.consultorId,
            consultorName: response.consultorName,
            keyUserId: response.usuarioChaveId,
            keyUserName: response.usuarioChaveName,
            projectName: response.projectName,
            projectAbbreviation: response.projectAbbreviation,
            clientName: response.clientName,
            clientAbbreviation: response.clientAbbreviation,
            functionalComplexity: response.functionalComplexity,
            techComplexity: response.techComplexity,
            estimatedDeliveryDate: response.estimatedDeliveryDate,
            startDate: response.startDate,
            devEndDate: response.devEndDate,
            efValidatedAt: response.efValidatedAt,
            category: response.category,
            jiraEpicKey: response.jiraEpicKey ?? null,
            jiraEpicUrl: response.jiraEpicUrl ?? null,
            jiraEpicSync: response.jiraEpicSync
        });
    }

    public static withFromResponseList(list: DevelopmentSummaryResponse[]): DevelopmentSummaryModel[] {
        return list.map(DevelopmentSummaryModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get displayId(): string {
        return this.props.displayId;
    }

    public get title(): string {
        return this.props.title;
    }

    public get status(): DevelopmentStatus {
        return this.props.status;
    }

    public get businessProcess(): string | null {
        return this.props.businessProcessName;
    }

    public get createdAt(): string {
        return this.props.createdAt;
    }

    public get updatedAt(): string {
        return this.props.updatedAt;
    }

    public get consultor(): { id: string; name: string } | null {
        return this.props.consultorId ? { id: this.props.consultorId, name: this.props.consultorName! } : null;
    }

    public get keyUser(): { id: string; name: string } | null {
        return this.props.keyUserId ? { id: this.props.keyUserId, name: this.props.keyUserName! } : null;
    }

    public get project(): { name: string; abbreviation: string; client: { name: string; abbreviation: string } } {
        return {
            name: this.props.projectName,
            abbreviation: this.props.projectAbbreviation,
            client: { name: this.props.clientName, abbreviation: this.props.clientAbbreviation }
        };
    }

    public get functionalComplexity(): Complexity | null {
        return this.props.functionalComplexity;
    }

    public get techComplexity(): Complexity | null {
        return this.props.techComplexity;
    }

    public get estimatedDeliveryDate(): string | null {
        return this.props.estimatedDeliveryDate;
    }

    public get startDate(): string | null {
        return this.props.startDate;
    }

    public get devEndDate(): string | null {
        return this.props.devEndDate;
    }

    public get efValidatedAt(): string | null {
        return this.props.efValidatedAt;
    }

    public get category(): DevelopmentCategory | null {
        return this.props.category;
    }

    public get jiraEpicKey(): string | null {
        return this.props.jiraEpicKey ?? null;
    }

    public get jiraEpicUrl(): string | null {
        return this.props.jiraEpicUrl ?? null;
    }

    public get jiraEpicSync(): JiraEpicSync {
        return this.props.jiraEpicSync ?? { status: this.jiraEpicKey ? 'LINKED' : 'NONE', nextAttemptAt: null };
    }

    public toObject(): DevelopmentSummaryProps {
        return { ...this.props };
    }
}

export type EfVersionResponse = {
    version: number;
    uploadedAt: string;
    uploadedBy: string;
    fileUrl: string;
    isCurrent: boolean;
};

export type EfVersionProps = {
    version: number;
    uploadedAt: string;
    uploadedBy: string;
    fileUrl: string;
    isCurrent: boolean;
};

export class EfVersionModel {
    private constructor(private readonly props: EfVersionProps) {}

    public static with(props: EfVersionProps): EfVersionModel {
        return new EfVersionModel(props);
    }

    public static withFromResponse(response: EfVersionResponse): EfVersionModel {
        return EfVersionModel.with({ ...response });
    }

    public static withFromResponseList(list: EfVersionResponse[]): EfVersionModel[] {
        return list.map(EfVersionModel.withFromResponse);
    }

    public get version(): number {
        return this.props.version;
    }

    public get uploadedAt(): string {
        return this.props.uploadedAt;
    }

    public get uploadedBy(): string {
        return this.props.uploadedBy;
    }

    public get fileUrl(): string {
        return this.props.fileUrl;
    }

    public get isCurrent(): boolean {
        return this.props.isCurrent;
    }

    public toObject(): EfVersionProps {
        return { ...this.props };
    }
}

export type AiAnalysisAdjustment = {
    field: string;
    suggestion: string;
    severity: string;
};

export type AiAnalysisPbiResponse = {
    id: string;
    sprint: number;
    userStory: string;
    userStoryRole: string;
    userStoryAction: string;
    userStoryBenefit: string;
    functionalDescription: string;
    acceptanceCriteria: Array<{ id: string; given: string; when: string; then: string }>;
    technicalNotes: string;
    businessRules: string[];
    dependsOn: string[];
};

export type AiAnalysisResponse = {
    id: string;
    attempt: number;
    status: 'APROVADA' | 'REJEITADA' | 'EM_ANDAMENTO';
    score: number;
    minScore: number;
    suggestedComplexity: Complexity | null;
    suggestedHours: number | null;
    summary: string;
    adjustments: AiAnalysisAdjustment[];
    pbiBacklog: AiAnalysisPbiResponse[] | null;
    analyzedAt: string | null;
    errorMessage: string | null;
};

export type EstimateResponse = {
    id: string;
    version: number;
    complexity: Complexity;
    estimatedHours: number;
    estimatedDelivery: string | null;
    notes: string;
    createdBy: { id: string; name: string };
    createdAt: string;
    isCurrent: boolean;
    isAiGenerated: boolean;
    rejectionReason: string | null;
    rejectedBy: { id: string; name: string } | null;
    rejectedAt: string | null;
};

export type EstimateProps = {
    id: string;
    version: number;
    complexity: Complexity;
    estimatedHours: number;
    estimatedDelivery: string | null;
    notes: string;
    createdById: string;
    createdByName: string;
    createdAt: string;
    isCurrent: boolean;
    isAiGenerated: boolean;
    rejectionReason: string | null;
    rejectedById: string | null;
    rejectedByName: string | null;
    rejectedAt: string | null;
};

export class EstimateModel {
    private constructor(private readonly props: EstimateProps) {}

    public static with(props: EstimateProps): EstimateModel {
        return new EstimateModel(props);
    }

    public static withFromResponse(response: EstimateResponse): EstimateModel {
        return EstimateModel.with({
            id: response.id,
            version: response.version,
            complexity: response.complexity,
            estimatedHours: response.estimatedHours,
            estimatedDelivery: response.estimatedDelivery,
            notes: response.notes,
            createdById: response.createdBy.id,
            createdByName: response.createdBy.name,
            createdAt: response.createdAt,
            isCurrent: response.isCurrent,
            isAiGenerated: response.isAiGenerated,
            rejectionReason: response.rejectionReason,
            rejectedById: response.rejectedBy?.id ?? null,
            rejectedByName: response.rejectedBy?.name ?? null,
            rejectedAt: response.rejectedAt
        });
    }

    public static withFromResponseList(list: EstimateResponse[]): EstimateModel[] {
        return list.map(EstimateModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get version(): number {
        return this.props.version;
    }

    public get complexity(): Complexity {
        return this.props.complexity;
    }

    public get estimatedHours(): number {
        return this.props.estimatedHours;
    }

    public get estimatedDelivery(): string | null {
        return this.props.estimatedDelivery;
    }

    public get notes(): string {
        return this.props.notes;
    }

    public get createdBy(): { id: string; name: string } {
        return { id: this.props.createdById, name: this.props.createdByName };
    }

    public get createdAt(): string {
        return this.props.createdAt;
    }

    public get isCurrent(): boolean {
        return this.props.isCurrent;
    }

    public get isAiGenerated(): boolean {
        return this.props.isAiGenerated;
    }

    public get rejectionReason(): string | null {
        return this.props.rejectionReason;
    }

    public get rejectedBy(): { id: string; name: string } | null {
        return this.props.rejectedById ? { id: this.props.rejectedById, name: this.props.rejectedByName! } : null;
    }

    public get rejectedAt(): string | null {
        return this.props.rejectedAt;
    }

    public toObject(): EstimateProps {
        return { ...this.props };
    }
}

export type CreateEstimateParams = {
    complexity: Complexity;
    estimatedHours: number;
    estimatedDeliveryDate?: string;
    notes?: string;
    developers?: Array<{ userId: string; isLead: boolean }>;
    effortIncreaseReasonId?: string;
    effortIncreaseNotes?: string;
};

export type DevelopmentDocumentResponse = {
    id: string;
    type: 'TECHNICAL_DOC' | 'TEST_EVIDENCE' | 'CUTOVER_DOC';
    fileName: string;
    fileSize: number;
    url: string;
    uploadedAt: string;
    uploadedBy: string;
};

export type DevelopmentDocumentProps = {
    id: string;
    type: 'TECHNICAL_DOC' | 'TEST_EVIDENCE' | 'CUTOVER_DOC';
    fileName: string;
    fileSize: number;
    url: string;
    uploadedAt: string;
    uploadedBy: string;
};

export class DevelopmentDocumentModel {
    private constructor(private readonly props: DevelopmentDocumentProps) {}

    public static with(props: DevelopmentDocumentProps): DevelopmentDocumentModel {
        return new DevelopmentDocumentModel(props);
    }

    public static withFromResponse(response: DevelopmentDocumentResponse): DevelopmentDocumentModel {
        return DevelopmentDocumentModel.with({ ...response });
    }

    public static withFromResponseList(list: DevelopmentDocumentResponse[]): DevelopmentDocumentModel[] {
        return list.map(DevelopmentDocumentModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get type(): 'TECHNICAL_DOC' | 'TEST_EVIDENCE' | 'CUTOVER_DOC' {
        return this.props.type;
    }

    public get fileName(): string {
        return this.props.fileName;
    }

    public get fileSize(): number {
        return this.props.fileSize;
    }

    public get url(): string {
        return this.props.url;
    }

    public get uploadedAt(): string {
        return this.props.uploadedAt;
    }

    public get uploadedBy(): string {
        return this.props.uploadedBy;
    }

    public toObject(): DevelopmentDocumentProps {
        return { ...this.props };
    }
}

export type ValidationEvidenceResponse = {
    id: string;
    fileName: string;
    fileSize: number;
    url: string;
    uploadedAt: string;
    uploadedBy: string;
};

export type ValidationEvidenceProps = {
    id: string;
    fileName: string;
    fileSize: number;
    url: string;
    uploadedAt: string;
    uploadedBy: string;
};

export class ValidationEvidenceModel {
    private constructor(private readonly props: ValidationEvidenceProps) {}

    public static with(props: ValidationEvidenceProps): ValidationEvidenceModel {
        return new ValidationEvidenceModel(props);
    }

    public static withFromResponse(response: ValidationEvidenceResponse): ValidationEvidenceModel {
        return ValidationEvidenceModel.with({ ...response });
    }

    public static withFromResponseList(list: ValidationEvidenceResponse[]): ValidationEvidenceModel[] {
        return list.map(ValidationEvidenceModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get fileName(): string {
        return this.props.fileName;
    }

    public get fileSize(): number {
        return this.props.fileSize;
    }

    public get url(): string {
        return this.props.url;
    }

    public get uploadedAt(): string {
        return this.props.uploadedAt;
    }

    public get uploadedBy(): string {
        return this.props.uploadedBy;
    }

    public toObject(): ValidationEvidenceProps {
        return { ...this.props };
    }
}

export type SolicitationResponse = {
    id: string;
    type: 'BUG' | 'AJUSTE_MINOR';
    title: string;
    description: string;
    stepsToReproduce: string | null;
    status: 'ABERTA' | 'APROVADA' | 'REJEITADA' | 'RESOLVIDA';
    openedBy: { id: string; name: string };
    openedAt: string;
    ltResponse: string | null;
    ltRespondedBy: { id: string; name: string } | null;
    ltRespondedAt: string | null;
    expectedFixDate: string | null;
    reclassifiedAs: 'BUG' | 'AJUSTE_MINOR' | null;
    rejectionNotes: string | null;
    resolutionNotes: string | null;
    resolvedAt: string | null;
    evidences: ValidationEvidenceResponse[];
};

export type SolicitationProps = {
    id: string;
    type: 'BUG' | 'AJUSTE_MINOR';
    title: string;
    description: string;
    stepsToReproduce: string | null;
    status: 'ABERTA' | 'APROVADA' | 'REJEITADA' | 'RESOLVIDA';
    openedById: string;
    openedByName: string;
    openedAt: string;
    ltResponse: string | null;
    ltRespondedById: string | null;
    ltRespondedByName: string | null;
    ltRespondedAt: string | null;
    expectedFixDate: string | null;
    reclassifiedAs: 'BUG' | 'AJUSTE_MINOR' | null;
    rejectionNotes: string | null;
    resolutionNotes: string | null;
    resolvedAt: string | null;
    evidences: ValidationEvidenceModel[];
};

export class SolicitationModel {
    private constructor(private readonly props: SolicitationProps) {}

    public static with(props: SolicitationProps): SolicitationModel {
        return new SolicitationModel(props);
    }

    public static withFromResponse(response: SolicitationResponse): SolicitationModel {
        return SolicitationModel.with({
            id: response.id,
            type: response.type,
            title: response.title,
            description: response.description,
            stepsToReproduce: response.stepsToReproduce,
            status: response.status,
            openedById: response.openedBy.id,
            openedByName: response.openedBy.name,
            openedAt: response.openedAt,
            ltResponse: response.ltResponse,
            ltRespondedById: response.ltRespondedBy?.id ?? null,
            ltRespondedByName: response.ltRespondedBy?.name ?? null,
            ltRespondedAt: response.ltRespondedAt,
            expectedFixDate: response.expectedFixDate,
            reclassifiedAs: response.reclassifiedAs,
            rejectionNotes: response.rejectionNotes,
            resolutionNotes: response.resolutionNotes,
            resolvedAt: response.resolvedAt,
            evidences: ValidationEvidenceModel.withFromResponseList(response.evidences ?? [])
        });
    }

    public static withFromResponseList(list: SolicitationResponse[]): SolicitationModel[] {
        return list.map(SolicitationModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get type(): 'BUG' | 'AJUSTE_MINOR' {
        return this.props.type;
    }

    public get title(): string {
        return this.props.title;
    }

    public get description(): string {
        return this.props.description;
    }

    public get stepsToReproduce(): string | null {
        return this.props.stepsToReproduce;
    }

    public get status(): 'ABERTA' | 'APROVADA' | 'REJEITADA' | 'RESOLVIDA' {
        return this.props.status;
    }

    public get openedBy(): { id: string; name: string } {
        return { id: this.props.openedById, name: this.props.openedByName };
    }

    public get openedAt(): string {
        return this.props.openedAt;
    }

    public get ltResponse(): string | null {
        return this.props.ltResponse;
    }

    public get ltRespondedBy(): { id: string; name: string } | null {
        return this.props.ltRespondedById ? { id: this.props.ltRespondedById, name: this.props.ltRespondedByName! } : null;
    }

    public get ltRespondedAt(): string | null {
        return this.props.ltRespondedAt;
    }

    public get expectedFixDate(): string | null {
        return this.props.expectedFixDate;
    }

    public get reclassifiedAs(): 'BUG' | 'AJUSTE_MINOR' | null {
        return this.props.reclassifiedAs;
    }

    public get rejectionNotes(): string | null {
        return this.props.rejectionNotes;
    }

    public get resolutionNotes(): string | null {
        return this.props.resolutionNotes;
    }

    public get resolvedAt(): string | null {
        return this.props.resolvedAt;
    }

    public get evidences(): ValidationEvidenceModel[] {
        return this.props.evidences;
    }

    public toObject(): SolicitationProps {
        return { ...this.props };
    }
}

export type CreateSolicitationParams = {
    type: 'BUG' | 'AJUSTE_MINOR';
    title: string;
    description: string;
    stepsToReproduce?: string;
};

export type AuditEntryResponse = {
    id: string;
    type: 'STATUS' | 'FIELD';
    fieldName?: string;
    fromStatus?: DevelopmentStatus;
    toStatus?: DevelopmentStatus;
    oldValue?: string | null;
    newValue?: string | null;
    changedBy: string | null;
    changedByName: string | null;
    changedAt: string;
    reasonId?: string | null;
    reasonLabel?: string | null;
    notes?: string | null;
};

export type AuditDataResponse = {
    data: AuditEntryResponse[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
};

export type AuditDataProps = {
    entries: AuditEntryResponse[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
};

export class AuditDataModel {
    private constructor(private readonly props: AuditDataProps) {}

    public static with(props: AuditDataProps): AuditDataModel {
        return new AuditDataModel(props);
    }

    public static withFromResponse(response: AuditDataResponse): AuditDataModel {
        return AuditDataModel.with({
            entries: response.data ?? [],
            total: response.total,
            page: response.page,
            limit: response.limit,
            totalPages: response.totalPages
        });
    }

    public get entries(): AuditEntryResponse[] {
        return this.props.entries;
    }

    public get total(): number {
        return this.props.total;
    }

    public toObject(): AuditDataProps {
        return { ...this.props };
    }
}

export type DeveloperAssignmentResponse = { userId: string; userName: string; isLead: boolean };

export type DevelopmentDetailDocumentResponse = {
    id: string;
    docType: 'ESPECIFICACAO_FUNCIONAL' | 'DOCUMENTACAO_TECNICA' | 'EVIDENCIA_TESTES_DEV' | 'DOCUMENTO_CUTOVER' | 'EVIDENCIA_TESTES_CF' | 'EVIDENCIA_TESTES_UC' | 'ANALISE_IA';
    fileName: string;
    fileUrl: string;
    fileSizeBytes: string | null;
    version: number;
    isCurrent: boolean;
    uploadedAt: string;
    uploadedBy: string | null;
    uploadedByName: string | null;
};

export type DevelopmentDetailEstimateResponse = {
    id: string;
    version: number;
    isFromAi: boolean;
    complexity: Complexity | null;
    estimatedHours: number | null;
    estimatedDeliveryDate: string | null;
    notes: string | null;
    effortIncreaseReasonId: string | null;
    effortIncreaseNotes: string | null;
    rejected: boolean;
    createdAt: string;
    createdBy: string | null;
};

export type SuggestedFixResponse = {
    description: string;
    severity: string;
    reference: string;
    pageRef?: string;
};

export type CriteriaScoreResponse = {
    code: string;
    name: string;
    weight: number;
    score: number;
    maxScore: number;
};

export type DevelopmentDetailAiAnalysisResponse = {
    id: string;
    attemptNumber: number;
    score: number;
    minScoreUsed: number;
    passed: boolean;
    analysisSummary: string | null;
    suggestedFixes: SuggestedFixResponse[] | null;
    suggestedComplexity: Complexity | null;
    suggestedHours: number | null;
    blockingQuestions: string[] | null;
    assumptions: string[] | null;
    criteriaScores: CriteriaScoreResponse[] | null;
    strictnessLevelUsed: number | null;
    modelUsed: string | null;
    providerUsed: string | null;
    extraPromptHash: string | null;
    efInputMode: string | null;
    analyzedAt: string;
    sapValidationMode?: 'live' | 'degraded' | null;
    sapValidationSystem?: string | null;
    sapValidationError?: string | null;
};

export type PbiDecompositionStatus = 'PROCESSANDO' | 'CONCLUIDO' | 'ERRO';

export type PbiUserStory = {
    as: string;
    iWant: string;
    soThat: string;
};

export type PbiItemResponse = {
    number: string;
    title: string;
    priority: number;
    estimate: string;
    suggestedSprint: number;
    businessRules: string[];
    dependencies: string[];
    userStory: PbiUserStory;
    description: string;
    acceptanceCriteria: unknown[];
};

export type PbiDecompositionResponse = {
    status: PbiDecompositionStatus;
    pbis: PbiItemResponse[] | null;
    errorMessage: string | null;
};

export type PbiDecompositionProps = PbiDecompositionResponse;

export class PbiDecompositionModel {
    private constructor(private readonly props: PbiDecompositionProps) {}

    public static with(props: PbiDecompositionProps): PbiDecompositionModel {
        return new PbiDecompositionModel(props);
    }

    public static withFromResponse(response: PbiDecompositionResponse): PbiDecompositionModel {
        return PbiDecompositionModel.with({ ...response });
    }

    public get status(): PbiDecompositionStatus {
        return this.props.status;
    }

    public get pbis(): PbiItemResponse[] | null {
        return this.props.pbis;
    }

    public get errorMessage(): string | null {
        return this.props.errorMessage;
    }

    public isProcessing(): boolean {
        return this.props.status === 'PROCESSANDO';
    }

    public isCompleted(): boolean {
        return this.props.status === 'CONCLUIDO';
    }

    public hasError(): boolean {
        return this.props.status === 'ERRO';
    }

    public toObject(): PbiDecompositionProps {
        return { ...this.props };
    }
}

export type AiAnalysisPhaseStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export type AiAnalysisPhaseEntryResponse = {
    key: string;
    status: AiAnalysisPhaseStatus;
    summary: string | null;
    startedAt: string | null;
    completedAt: string | null;
    errorMessage: string | null;
};

export type AiAnalysisProgressResponse = {
    attemptNumber: number;
    status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
    currentPhaseIndex: number;
    startedAt: string;
    phases: AiAnalysisPhaseEntryResponse[];
    errorMessage: string | null;
};

export type AiAnalysisProgressProps = AiAnalysisProgressResponse;

export class AiAnalysisProgressModel {
    private constructor(private readonly props: AiAnalysisProgressProps) {}

    public static with(props: AiAnalysisProgressProps): AiAnalysisProgressModel {
        return new AiAnalysisProgressModel(props);
    }

    public static withFromResponse(response: AiAnalysisProgressResponse): AiAnalysisProgressModel {
        return AiAnalysisProgressModel.with({ ...response });
    }

    public get attemptNumber(): number {
        return this.props.attemptNumber;
    }

    public get status(): AiAnalysisProgressResponse['status'] {
        return this.props.status;
    }

    public get currentPhaseIndex(): number {
        return this.props.currentPhaseIndex;
    }

    public get startedAt(): string {
        return this.props.startedAt;
    }

    public get phases(): AiAnalysisPhaseEntryResponse[] {
        return this.props.phases;
    }

    public get errorMessage(): string | null {
        return this.props.errorMessage;
    }

    public getLatestCompletedSummary(): string | null {
        const completed = [...this.props.phases].reverse().find((phase) => phase.status === 'COMPLETED' && phase.summary);
        return completed?.summary ?? null;
    }

    public toObject(): AiAnalysisProgressProps {
        return { ...this.props };
    }
}

export type DevelopmentDetailSolicitationResponse = {
    id: string;
    type: 'BUG' | 'AJUSTE_MINOR';
    status: 'ABERTA' | 'APROVADA' | 'REJEITADA' | 'RESOLVIDA';
    title: string;
    description: string;
    steps: string | null;
    ltResponse: string | null;
    rejectionNotes: string | null;
    reclassifiedType: 'BUG' | 'AJUSTE_MINOR' | null;
    expectedFixDate: string | null;
    createdAt: string;
    jiraSync?: JiraItemSync;
};

export type DevelopmentDetailDataResponse = {
    id: string;
    displayId: string;
    projectId: string;
    title: string;
    description: string | null;
    status: DevelopmentStatus;
    lastActiveStatus: DevelopmentStatus | null;
    businessProcessId: string | null;
    consultorId: string | null;
    usuarioChaveId: string | null;
    liderTecnicoId: string | null;
    linkedDevelopmentId: string | null;
    techComplexity: Complexity | null;
    techHours: number | null;
    functionalComplexity: Complexity | null;
    functionalHours: number | null;
    cancelReason: string | null;
    cancelledAt: string | null;
    startDate: string | null;
    devEndDate: string | null;
    createdAt: string;
    updatedAt: string;
    projectName: string;
    projectAbbreviation: string;
    clientName: string;
    clientAbbreviation: string;
    sapConnectionConfigured?: boolean;
    businessProcessName: string | null;
    consultorName: string | null;
    usuarioChaveName: string | null;
    liderTecnicoName: string | null;
    developers: DeveloperAssignmentResponse[];
    documents: DevelopmentDetailDocumentResponse[];
    estimates: DevelopmentDetailEstimateResponse[];
    aiAnalyses: DevelopmentDetailAiAnalysisResponse[];
    solicitations: DevelopmentDetailSolicitationResponse[];
    category: DevelopmentCategory | null;
    cutoverNotRequired: boolean;
    jiraEpicKey?: string | null;
    jiraEpicUrl?: string | null;
    jiraEpicStatusName?: string | null;
    jiraLastError?: string | null;
    jiraEpicCreateOpen?: boolean;
    jiraEpicSync?: JiraEpicSync;
    jiraStoriesSync?: JiraStoriesSync;
};

export type DevelopmentDetailDataProps = DevelopmentDetailDataResponse;

export class DevelopmentDetailDataModel {
    private readonly _efVersions: DevelopmentDetailDocumentResponse[];
    private readonly _currentEstimate: DevelopmentDetailEstimateResponse | null;

    private constructor(private readonly props: DevelopmentDetailDataProps) {
        this._efVersions = props.documents.filter((d) => d.docType === 'ESPECIFICACAO_FUNCIONAL').sort((a, b) => a.version - b.version);
        const activeEstimates = props.estimates.filter((e) => !e.rejected);
        const estimatePool = activeEstimates.length > 0 ? activeEstimates : props.estimates;
        this._currentEstimate = estimatePool.reduce<DevelopmentDetailEstimateResponse | null>((latest, e) => (!latest || e.version > latest.version ? e : latest), null);
    }

    public static with(props: DevelopmentDetailDataProps): DevelopmentDetailDataModel {
        return new DevelopmentDetailDataModel(props);
    }

    public static withFromResponse(response: DevelopmentDetailDataResponse): DevelopmentDetailDataModel {
        return DevelopmentDetailDataModel.with({ ...response });
    }

    public get id(): string {
        return this.props.id;
    }

    public get displayId(): string {
        return this.props.displayId;
    }

    public get projectId(): string {
        return this.props.projectId;
    }

    public get liderTecnicoId(): string | null {
        return this.props.liderTecnicoId;
    }

    public get businessProcessId(): string | null {
        return this.props.businessProcessId;
    }

    public get consultorId(): string | null {
        return this.props.consultorId;
    }

    public get usuarioChaveId(): string | null {
        return this.props.usuarioChaveId;
    }

    public get lastActiveStatus(): DevelopmentStatus | null {
        return this.props.lastActiveStatus;
    }

    public get title(): string {
        return this.props.title;
    }

    public get description(): string | null {
        return this.props.description;
    }

    public get status(): DevelopmentStatus {
        return this.props.status;
    }

    public get projectName(): string {
        return this.props.projectName;
    }

    public get projectAbbreviation(): string {
        return this.props.projectAbbreviation;
    }

    public get clientName(): string {
        return this.props.clientName;
    }

    public get clientAbbreviation(): string {
        return this.props.clientAbbreviation;
    }

    public get sapConnectionConfigured(): boolean {
        return this.props.sapConnectionConfigured !== false;
    }

    public get businessProcessName(): string | null {
        return this.props.businessProcessName;
    }

    public get consultorName(): string | null {
        return this.props.consultorName;
    }

    public get usuarioChaveName(): string | null {
        return this.props.usuarioChaveName;
    }

    public get liderTecnicoName(): string | null {
        return this.props.liderTecnicoName;
    }

    public get linkedDevelopmentId(): string | null {
        return this.props.linkedDevelopmentId;
    }

    public get techComplexity(): Complexity | null {
        return this.props.techComplexity;
    }

    public get techHours(): number | null {
        return this.props.techHours;
    }

    public get functionalComplexity(): Complexity | null {
        return this.props.functionalComplexity;
    }

    public get functionalHours(): number | null {
        return this.props.functionalHours;
    }

    public get cancelReason(): string | null {
        return this.props.cancelReason;
    }

    public get cancelledAt(): string | null {
        return this.props.cancelledAt;
    }

    public get startDate(): string | null {
        return this.props.startDate;
    }

    public get devEndDate(): string | null {
        return this.props.devEndDate;
    }

    public get createdAt(): string {
        return this.props.createdAt;
    }

    public get updatedAt(): string {
        return this.props.updatedAt;
    }

    public get developers(): DeveloperAssignmentResponse[] {
        return this.props.developers;
    }

    public get documents(): DevelopmentDetailDocumentResponse[] {
        return this.props.documents;
    }

    public get estimates(): DevelopmentDetailEstimateResponse[] {
        return this.props.estimates;
    }

    public get aiAnalyses(): DevelopmentDetailAiAnalysisResponse[] {
        return this.props.aiAnalyses;
    }

    public get solicitations(): DevelopmentDetailSolicitationResponse[] {
        return this.props.solicitations;
    }

    public get efVersions(): DevelopmentDetailDocumentResponse[] {
        return this._efVersions;
    }

    public get currentEfUrl(): string | null {
        return this._efVersions.find((d) => d.isCurrent)?.fileUrl ?? null;
    }

    public get currentEfDocument(): DevelopmentDetailDocumentResponse | null {
        return this._efVersions.find((d) => d.isCurrent) ?? null;
    }

    public get currentEstimate(): DevelopmentDetailEstimateResponse | null {
        return this._currentEstimate;
    }

    public get category(): DevelopmentCategory | null {
        return this.props.category;
    }

    public get cutoverNotRequired(): boolean {
        return this.props.cutoverNotRequired;
    }

    public get jiraEpicKey(): string | null {
        return this.props.jiraEpicKey ?? null;
    }

    public get jiraEpicUrl(): string | null {
        return this.props.jiraEpicUrl ?? null;
    }

    public get jiraEpicStatusName(): string | null {
        return this.props.jiraEpicStatusName ?? null;
    }

    public get jiraLastError(): string | null {
        return this.props.jiraLastError ?? null;
    }

    public get jiraEpicCreateOpen(): boolean {
        return this.props.jiraEpicCreateOpen === true;
    }

    public get jiraEpicSync(): JiraEpicSync {
        return this.props.jiraEpicSync ?? { status: this.jiraEpicKey ? 'LINKED' : 'NONE', nextAttemptAt: null };
    }

    public get jiraStoriesSync(): JiraStoriesSync {
        return this.props.jiraStoriesSync ?? { status: 'NONE', created: 0, total: 0, nextAttemptAt: null };
    }

    public toObject(): DevelopmentDetailDataProps {
        return { ...this.props };
    }
}

export type JiraEpicOption = {
    key: string;
    summary: string;
};

export type JiraEpicSuggestion = {
    suggestion: JiraEpicOption | null;
    epics: JiraEpicOption[];
};

export type JiraEpicLink = {
    jiraEpicKey: string;
    jiraEpicUrl: string;
};

export type JiraNoPairResult = {
    jiraWarning: string | null;
};

export type CreateDevelopmentParams = {
    title: string;
    description: string;
    businessProcessId?: string;
    projectId: string;
    consultorId?: string;
    usuarioChaveId?: string;
    linkedDevelopmentId?: string;
    functionalComplexity: Complexity | null;
    functionalHours: number | null;
    techComplexity: Complexity | null;
    techHours: number | null;
    estimatedDeliveryDate: string | null;
    category: DevelopmentCategory | null;
    jiraEpicKey?: string | null;
};

export type UpdateDevelopmentParams = {
    title?: string;
    description?: string;
    businessProcessId?: string | null;
    consultorId?: string;
    usuarioChaveId?: string;
    linkedDevelopmentId?: string | null;
    functionalComplexity?: Complexity | null;
    functionalHours?: number | null;
    category?: DevelopmentCategory | null;
};

export type CancelDevelopmentParams = {
    reasonId: string;
    justification: string;
};

export type ImpedeDevelopmentParams = {
    reasonId: string;
    description: string;
};

export type StartDevelopmentParams = {
    startDate: string;
    developers?: Array<{ userId: string; isLead: boolean }>;
};
